import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { User } from '../models/User';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';
import { StaffService } from './staff.service';

export interface UserCreate {
  name: string;
  email: string;
  password?: string;
  salt?: string;
  emailConfirmationCode?: string;
  staffId?: number;
  profileIds: number[]; // Array de IDs de perfis
  active: boolean;
  blocked: boolean;
  employeeType: 'CLT' | 'PJ';
  isClient: boolean;
  isSeller: boolean;
  staffId1?: number;
  useSenhaPadrao?: boolean;
  createdBy: string;
}

export interface UserUpdate {
  name?: string;
  email?: string;
  password?: string;
  staffId?: number;
  profileIds?: number[]; // Array de IDs de perfis
  active?: boolean;
  blocked?: boolean;
  employeeType?: 'CLT' | 'PJ';
  isClient?: boolean;
  isSeller?: boolean;
  staffId1?: number;
  updatedBy: string;
}

export interface UserSearchResult {
  id: number;
  nome: string;
  email: string;
  cpf?: string;
  tipo: string;
  cargo?: string;
  departamento?: string;
  empresa?: string;
  telefone?: string;
}

export interface ApiResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface AvatarResponse {
  userId: number;
  avatar: string;
}

export interface AvatarUploadRequest {
  avatarBase64: string;
  updatedBy: string;
}

export interface DashboardConsolidated {
  TotalUsers: number;
  ActiveUsers: number;
  BlockedUsers: number;
  ProfileDistribution: { [key: string]: number };
  ContractTypeDistribution: { [key: string]: number };
}

export const DEFAULT_TEMP_PASSWORD = 'Mudar@123';

export interface SetTemporaryPasswordRequest {
  email: string;
  tempPassword: string;
  updatedBy: string;
}

export interface ValidateTemporaryPasswordLoginResponse {
  success: boolean;
  requestedPassword: boolean;
  userId?: number;
  email?: string;
}

export interface ChangePasswordAfterTemporaryRequest {
  email: string;
  newPassword: string;
  updatedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private baseUrl = environment.microservices.user;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    public staffService?: StaffService // opcional para injeção cruzada
  ) {}

  // ===== CRUD de Usuários =====

  /**
   * Busca todos os usuários
   */
  getAll(): Observable<User[]> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/GetUsers`).pipe(
      map(response => response.data.map(u => this.mapFromUserDTO(u))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuários');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca usuário por ID
   */
  getById(id: number): Observable<User> {
    return this.http.get<any>(`${this.baseUrl}/GetUserById/${id}`).pipe(
      map(u => this.mapFromUserDTO(u)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca usuário por ID com informações completas incluindo dados do Staff
   */
  getUserById(userId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/GetUserById/${userId}`).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar dados do usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria um novo usuário
   */
  create(userData: UserCreate): Observable<User> {
    const dto = this.mapToUserDTO(userData);
    
    return this.http.post<any>(`${this.baseUrl}/CreateUser`, dto).pipe(
      map(u => {
        return this.mapFromUserDTO(u);
      }),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao criar usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um usuário existente
   */
  update(id: number, userData: UserUpdate): Observable<User> {
    const dto = this.mapToUserDTO(userData);
    console.log('DTO enviado para atualização:', dto); // Log para depuração
    return this.http.put<any>(`${this.baseUrl}/UpdateUser/${id}`, dto).pipe(
      map(u => this.mapFromUserDTO(u)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao atualizar usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove um usuário
   */
  delete(id: number, deletedBy: string): Observable<void> {
    const body = { DeletedBy: deletedBy };
    return this.http.request<void>('delete', `${this.baseUrl}/DeleteUser/${id}`, { body }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao excluir usuário');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Busca e Filtro =====

  /**
   * Busca usuários ativos
   */
  getActive(): Observable<User[]> {
    const params = new HttpParams().set('status', 'ativo');
    return this.http.get<any[]>(`${this.baseUrl}/GetUsers`, { params }).pipe(
      map(users => users.map(u => this.mapFromUserDTO(u))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuários ativos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca usuário por username
   */
  getByUsername(username: string): Observable<User | null> {
    const params = new HttpParams().set('username', username);
    return this.http.get<any>(`${this.baseUrl}/GetUserByUsername`, { params }).pipe(
      map(u => u ? this.mapFromUserDTO(u) : null),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuário por username');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca usuário por email
   */
  getByEmail(email: string): Observable<User | null> {
    const params = new HttpParams().set('email', email);
    return this.http.get<any>(`${this.baseUrl}/GetUserByEmail`, { params }).pipe(
      map(u => u ? this.mapFromUserDTO(u) : null),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuário por e-mail');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca usuários por tipo de vínculo
   */
  getByTipoVinculo(tipo: string): Observable<User[]> {
    const params = new HttpParams().set('tipoVinculo', tipo);
    return this.http.get<any[]>(`${this.baseUrl}/GetUsers`, { params }).pipe(
      map(users => users.map(u => this.mapFromUserDTO(u))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuários por tipo de vínculo');
        return throwError(() => err);
      })
    );
  }

  // ===== Busca de Pessoas para Vinculação =====

  /**
   * Busca pessoas/funcionários/clientes para vinculação com usuário
   */
  searchPessoas(searchTerm: string = ''): Observable<UserSearchResult[]> {
    const params = new HttpParams().set('search', searchTerm);
    return this.http.get<any[]>(`${this.baseUrl}/SearchPessoas`, { params }).pipe(
      map(pessoas => pessoas.map(p => this.mapFromPessoaDTO(p))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar pessoas');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca pessoas por tipo (funcionario, cliente, fornecedor, parceiro)
   */
  searchPessoasByTipo(tipo: string, searchTerm: string = ''): Observable<UserSearchResult[]> {
    const params = new HttpParams()
      .set('tipo', tipo)
      .set('search', searchTerm);
    return this.http.get<any[]>(`${this.baseUrl}/SearchPessoasByTipo`, { params }).pipe(
      map(pessoas => pessoas.map(p => this.mapFromPessoaDTO(p))),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar pessoas por tipo');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Gerenciamento de Senha =====

  /**
   * Gera e envia senha padrão por email
   */
  generateDefaultPassword(userId: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}/GenerateDefaultPassword/${userId}`, 
      {}
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao gerar senha padrão');
        return throwError(() => err);
      })
    );
  }

  /**
   * Solicita reset de senha
   */
  requestPasswordReset(email: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}/RequestPasswordReset`, 
      { email }
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao solicitar redefinição de senha');
        return throwError(() => err);
      })
    );
  }

  /**
   * Altera senha do usuário
   */
  changePassword(userId: number, oldPassword: string, newPassword: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${this.baseUrl}/ChangePassword/${userId}`, 
      { oldPassword, newPassword }
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao alterar senha');
        return throwError(() => err);
      })
    );
  }

  /**
   * Define senha temporária para um usuário já cadastrado (ação de admin)
   */
  setTemporaryPassword(email: string, tempPassword: string, updatedBy: string): Observable<{ success: boolean; message?: string }> {
    const body: SetTemporaryPasswordRequest = { email, tempPassword, updatedBy };
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.baseUrl}/SetTemporaryPassword`,
      body
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao definir senha temporária');
        return throwError(() => err);
      })
    );
  }

  /**
   * Valida login com senha temporária
   */
  validateTemporaryPasswordLogin(
    email: string,
    password: string,
    options?: { silent?: boolean }
  ): Observable<ValidateTemporaryPasswordLoginResponse> {
    return this.http.post<ValidateTemporaryPasswordLoginResponse>(
      `${this.baseUrl}/ValidateTemporaryPasswordLogin`,
      { email, password }
    ).pipe(
      map(response => ({
        success: !!response?.success,
        requestedPassword: !!response?.requestedPassword,
        userId: response?.userId,
        email: response?.email
      })),
      catchError(err => {
        if (options?.silent) {
          return of({ success: false, requestedPassword: false });
        }
        this.errorService.handleHttpError(err, 'Erro ao validar senha temporária');
        return throwError(() => err);
      })
    );
  }

  /**
   * Altera senha após login com senha temporária
   */
  changePasswordAfterTemporary(email: string, newPassword: string, updatedBy: string): Observable<{ success: boolean; message?: string }> {
    const body: ChangePasswordAfterTemporaryRequest = { email, newPassword, updatedBy };
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.baseUrl}/ChangePasswordAfterTemporary`,
      body
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao alterar senha após senha temporária');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Validação =====

  /**
   * Verifica se username está disponível
   */
  checkUsernameAvailability(username: string, excludeUserId?: number): Observable<{ available: boolean }> {
    let params = new HttpParams().set('username', username);
    if (excludeUserId) {
      params = params.set('excludeUserId', excludeUserId.toString());
    }
    
    return this.http.get<{ available: boolean }>(
      `${this.baseUrl}/CheckUsernameAvailability`, 
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao verificar disponibilidade de username');
        return throwError(() => err);
      })
    );
  }

  /**
   * Verifica se email está disponível
   */
  checkEmailAvailability(email: string, excludeUserId?: number): Observable<{ available: boolean }> {
    let params = new HttpParams().set('email', email);
    if (excludeUserId) {
      params = params.set('excludeUserId', excludeUserId.toString());
    }
    
    return this.http.get<{ available: boolean }>(
      `${this.baseUrl}/CheckEmailAvailability`, 
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao verificar disponibilidade de e-mail');
        return throwError(() => err);
      })
    );
  }

  // ===== Mapeamento de Dados =====

  /**
   * Mapeia dados recebidos da API para o modelo User
   */
  private mapFromUserDTO(dto: any): User {
    // Mapear ProfileIds (array) para profileIds
    const profileIds = dto.ProfileIds || dto.profileIds || [];
    // Para compatibilidade, pegar o primeiro ID como profileId
    const profileId = Array.isArray(profileIds) && profileIds.length > 0 ? profileIds[0] : 1;
    
    return {
      id: dto.Id || dto.id,
      name: dto.Name || dto.name || '',
      email: dto.Email || dto.email || '',
      salt: dto.Salt || dto.salt,
      emailConfirmationCode: dto.EmailConfirmationCode || dto.emailConfirmationCode,
      staffId: dto.StaffId || dto.staffId,
      profileId: profileId, // Primeiro ID do array ou 1 como fallback
      profileIds: profileIds, // Array completo de IDs
      active: dto.Active !== undefined ? dto.Active : (dto.active !== undefined ? dto.active : true),
      blocked: dto.Blocked !== undefined ? dto.Blocked : (dto.blocked !== undefined ? dto.blocked : false),
      employeeType: dto.EmployeeType || dto.employeeType || 'CLT',
      isClient: dto.IsClient !== undefined ? dto.IsClient : (dto.isClient !== undefined ? dto.isClient : false),
      isSeller: dto.IsSeller !== undefined ? dto.IsSeller : (dto.isSeller !== undefined ? dto.isSeller : false),
      staffId1: dto.StaffId1 || dto.staffId1,
      lastAccessDate: dto.LastAccessDate || dto.lastAccessDate ? new Date(dto.LastAccessDate || dto.lastAccessDate) : undefined,
      createdAt: dto.CreatedAt || dto.createdAt ? new Date(dto.CreatedAt || dto.createdAt) : undefined,
      updatedAt: dto.UpdatedAt || dto.updatedAt ? new Date(dto.UpdatedAt || dto.updatedAt) : undefined,
      createdBy: dto.CreatedBy || dto.createdBy,
      updatedBy: dto.UpdatedBy || dto.updatedBy,
      isDeleted: dto.IsDeleted !== undefined ? dto.IsDeleted : (dto.isDeleted !== undefined ? dto.isDeleted : false),
      deletedAt: dto.DeletedAt || dto.deletedAt ? new Date(dto.DeletedAt || dto.deletedAt) : undefined,
      deletedBy: dto.DeletedBy || dto.deletedBy
    };
  }

  /**
   * Mapeia dados do modelo User para envio à API
   */
  private mapToUserDTO(payload: any): any {
    const dto = {
      name: payload.name,
      email: payload.email,
      password: payload.password,
      salt: payload.salt || 'salt_teste_123',
      emailConfirmationCode: payload.emailConfirmationCode || '',
      staffId: payload.staffId || null,
      profileIds: payload.profileIds || [], // Array de IDs de perfis
      active: payload.active !== undefined ? payload.active : true,
      blocked: payload.blocked !== undefined ? payload.blocked : false,
      employeeType: payload.employeeType || 'CLT',
      isClient: payload.isClient !== undefined ? payload.isClient : false,
      isSeller: payload.isSeller !== undefined ? payload.isSeller : false,
      staffId1: payload.staffId1 || payload.staffId || null,
      createdBy: payload.createdBy || undefined,
      updatedBy: payload.updatedBy || undefined
    };
    return dto;
  }

  /**
   * Mapeia dados de pessoa recebidos da API para UserSearchResult
   */
  private mapFromPessoaDTO(dto: any): UserSearchResult {
    return {
      id: dto.Id || dto.id,
      nome: dto.Nome || dto.nome || dto.Name || dto.name || '',
      email: dto.Email || dto.email || '',
      cpf: dto.CPF || dto.cpf || dto.DocumentNumber || dto.documentNumber,
      tipo: dto.Tipo || dto.tipo || dto.Type || dto.type || '',
      cargo: dto.Cargo || dto.cargo || dto.Position || dto.position,
      departamento: dto.Departamento || dto.departamento || dto.Department || dto.department,
      empresa: dto.Empresa || dto.empresa || dto.Company || dto.company,
      telefone: dto.Telefone || dto.telefone || dto.Phone || dto.phone
    };
  }

  // ===== Métodos Adicionais da Coleção Postman =====

  /**
   * Busca usuário por ID específico
   */
  getByIdSpecific(id: number): Observable<User> {
    return this.http.get<any>(`${this.baseUrl}/GetUsers/${id}`).pipe(
      map(u => this.mapFromUserDTO(u)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca usuários com paginação
   */
  getUsersPaginated(page: number = 1, pageSize: number = 10, filters?: {
    active?: string;
    employeeType?: string;
    isClient?: string;
    profileId?: string;
  }): Observable<ApiResponse<User>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (filters?.active !== undefined && filters.active !== '') {
      params = params.set('active', filters.active);
    }
    if (filters?.employeeType !== undefined && filters.employeeType !== '') {
      params = params.set('employeeType', filters.employeeType);
    }
    if (filters?.isClient !== undefined && filters.isClient !== '') {
      params = params.set('isClient', filters.isClient);
    }
    if (filters?.profileId !== undefined && filters.profileId !== '') {
      params = params.set('profileId', filters.profileId);
    }

    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/GetUsers`, { params }).pipe(
      map(response => ({
        data: response.data.map(u => this.mapFromUserDTO(u)),
        pagination: response.pagination
      })),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar usuários paginados');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza usuário com JWT
   */
  updateWithJWT(id: number, userData: UserUpdate, jwtToken: string): Observable<User> {
    const dto = this.mapToUserDTO(userData);
    const headers = { 'Authorization': `Bearer ${jwtToken}` };
    
    return this.http.put<any>(`${this.baseUrl}/UpdateUser/${id}`, dto, { headers }).pipe(
      map(u => this.mapFromUserDTO(u)),
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao atualizar usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove usuário com header X-User-Id
   */
  deleteWithHeader(id: number, userId: string): Observable<void> {
    const headers = { 'X-User-Id': userId };
    
    return this.http.delete<void>(`${this.baseUrl}/DeleteUser/${id}`, { headers }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao excluir usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove usuário com deletedBy no body
   */
  deleteWithBody(id: number, deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeleteUser/${id}`, {
      body: { deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao excluir usuário');
        return throwError(() => err);
      })
    );
  }

  /**
   * Valida senha forte
   */
  validatePassword(password: string): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Senha deve ter pelo menos 8 caracteres');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Senha deve conter pelo menos uma letra maiúscula');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Senha deve conter pelo menos uma letra minúscula');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Senha deve conter pelo menos um número');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Senha deve conter pelo menos um caractere especial');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Valida formato de email
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // ===== Métodos de Avatar =====

  /**
   * Faz upload do avatar do usuário (base64)
   */
  uploadAvatar(userId: number, avatarBase64: string, updatedBy?: string): Observable<any> {
    const url = `${this.baseUrl}/UploadAvatar/${userId}`;
    const request: AvatarUploadRequest = {
      avatarBase64,
      updatedBy: updatedBy || userId.toString()
    };
    return this.http.post(url, request).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao fazer upload do avatar');
        return throwError(() => err);
      })
    );
  }

  /**
   * Obtém o avatar do usuário
   */
  getAvatar(userId: number): Observable<AvatarResponse> {
    const url = `${this.baseUrl}/GetAvatar/${userId}`;
    return this.http.get<AvatarResponse>(url).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza o avatar do usuário (base64)
   */
  updateAvatar(userId: number, avatarBase64: string, updatedBy?: string): Observable<any> {
    const url = `${this.baseUrl}/UpdateAvatar/${userId}`;
    const request: AvatarUploadRequest = {
      avatarBase64,
      updatedBy: updatedBy || userId.toString()
    };
    return this.http.put(url, request).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao atualizar avatar');
        return throwError(() => err);
      })
    );
  }

  // ===== Métodos de Dashboard =====

  /**
   * Obtém dados consolidados do dashboard de usuários
   */
  getDashboardConsolidated(): Observable<DashboardConsolidated> {
    const url = `${this.baseUrl}/Dashboard/Consolidated`;
    return this.http.get<DashboardConsolidated>(url).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao obter dados consolidados do dashboard');
        return throwError(() => err);
      })
    );
  }
}
